import React from 'react'
import { t } from '@lingui/core/macro'
import {
  Button,
  DatePickerInput,
  Loading,
  Table,
  TCell,
  Td,
  THead,
  THeadCell,
  TRow,
  TRowHeader
} from '@tecsinapse/cortex-react'
import { parseISO } from 'date-fns'
import { LiaMinusCircleSolid, LiaPlusCircleSolid } from 'react-icons/lia'
import type { OptionSelect } from '@/core/utils/types'
import { Select } from '@/core/components/select'
import { DepartamentoOption } from '@/core/hooks/use-departamentos'
import { useCargosUsuarioTable } from '../../hooks/use-table'
import { CargoUsuarioForm } from '../../types/cargo'

export interface ICargosUsuarioTable {
  showDepartamentos: boolean
  departamentoOptions: DepartamentoOption[]
  dealer: OptionSelect
  append: (args: CargoUsuarioForm) => void
  remove: (idx: number) => void
  values: any
  children?: React.ReactNode
}

const CargosUsuarioTable = ({
  showDepartamentos = false,
  departamentoOptions,
  dealer,
  append,
  remove,
  values,
  children
}: ICargosUsuarioTable) => {
  const {
    cargosOptions,
    setCargo,
    cargo,
    setDepartamento,
    departamento,
    handleInsert,
    validating,
    cargosUsuarioByDealer,
    handleRemove,
    date,
    setDate
  } = useCargosUsuarioTable({
    dealer,
    append,
    remove,
    values,
    showDepartamentos
  })

  return (
    <Table className='w-full'>
      <THead>
        {children}
        <TRow>
          <Td colSpan={99}>
            <div className='flex flex-row gap-3'>
              <Select
                options={cargosOptions || []}
                keyExtractor={(v: OptionSelect) => v.value}
                labelExtractor={(v: OptionSelect) => v.label}
                onSelect={setCargo}
                value={cargo}
                label={t`Cargo`}
                placeholder={t`Selecione o Cargo`}
              />
              {showDepartamentos && (
                <Select
                  options={departamentoOptions}
                  keyExtractor={(v: OptionSelect) => v.value}
                  labelExtractor={(v: OptionSelect) => v.label}
                  onSelect={setDepartamento}
                  value={departamento}
                  label={t`Departamento`}
                  placeholder={t`Selecione o Departamento`}
                />
              )}
              <div className='w-[470px]'>
                <DatePickerInput
                  variants={{ intent: 'default' }}
                  onChange={_date => {
                    setDate(_date)
                  }}
                  value={date}
                  label={t`Data de início`}
                  granularity={'day'}
                  hourCycle={24}
                />
              </div>
              <Button
                variants={{
                  size: 'square',
                  className: 'min-w-fit h-full flex items-center justify-center'
                }}
                onClick={() => handleInsert(dealer, cargo, departamento, date)}
                type='button'
                title='Adicionar'
                disabled={validating}
              >
                {validating ? (
                  <Loading className='w-3 h-3' />
                ) : (
                  <LiaPlusCircleSolid />
                )}
              </Button>
            </div>
          </Td>
        </TRow>

        <TRowHeader>
          <THeadCell>{t`Cargo`}</THeadCell>
          {showDepartamentos && <THeadCell>{t`Departamento`}</THeadCell>}
          <THeadCell>{t`Data de início`}</THeadCell>
          <THeadCell className='flex justify-end'>{t`Ações`}</THeadCell>
        </TRowHeader>
      </THead>
      <tbody>
        {cargosUsuarioByDealer.map(it => (
          <TRow key={`${it.value}-${it.dealerId}`}>
            <TCell>{it.label}</TCell>
            {showDepartamentos && <TCell>{it.departamentoNome}</TCell>}
            <TCell>
              {it.dataInicio
                ? parseISO(it.dataInicio).toLocaleDateString()
                : ''}
            </TCell>
            <TCell className='flex justify-end'>
              <button
                className='bg-transparent'
                onClick={() => handleRemove(it.value, it.dealerId)}
                type='button'
                title='Remover'
              >
                <LiaMinusCircleSolid size={16} />
              </button>
            </TCell>
          </TRow>
        ))}
      </tbody>
    </Table>
  )
}

export default CargosUsuarioTable
